/* eslint-disable no-console */
import React, { useState, useEffect, useCallback } from 'react'
import { get } from 'lodash'
import { useHistory, useParams } from 'react-router-dom'
import {
  sendErrorReport,
  displayValue,
  isFeatureAllowed,
  checkUserPermission
} from 'shared/helpers'
import {
  Button,
  Forbidden,
  IconSearch,
  IconFilter,
  List,
  Notification,
  Page,
  PermissionMissingNotificationTitle,
  Selector,
  TextInput,
  MetadataFilter
} from 'shared/components'
import { platformFeatures, searchType, userPermissions } from 'shared/constants'
import { fetchUserGroups } from 'src/LicenseUsers/actions'
import UserGroupForm from './UserGroupForm'
import './styles.scss'

const UserGroups = () => {
  const canManageCustomers = checkUserPermission(userPermissions.customers_write)
  const history = useHistory()
  const { companyId } = useParams()

  const [isLoading, setLoading] = useState(true)
  const [list, setList] = useState([])
  // table state
  const [listCount, setListCount] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [currentSort, setSort] = useState([
    {
      desc: false,
      id: 'name'
    }
  ])
  // search state
  const [query, setQuery] = useState('')
  const [activeQuery, setActiveQuery] = useState('')
  const [searchBy, setSearchBy] = useState(get(searchType, 'all'))
  // metadata filter state
  const [isMetadataFilterDisplayed, setMetadataFilterDisplayed] = useState(false)
  const [metadataFilterQuery, setMetadataFilterQuery] = useState('')
  // forms state
  const [isFormDisplayed, setFormDisplay] = useState(false)

  const getUserGroups = useCallback(
    (
      tablePage = 0,
      activeQ = undefined,
      searchTypeSelect = undefined,
      sort = currentSort,
      rows = rowsPerPage,
      metadataFilter = metadataFilterQuery
    ) => {
      setLoading(true)
      // Add metadata filter to the API call
      const params = metadataFilter ? { metadata_filter: metadataFilter } : {}

      fetchUserGroups(companyId, tablePage, activeQ, searchTypeSelect, sort, rows, params)
        .then(res => {
          setList(get(res, 'data.results') || [])
          setListCount(get(res, 'data.count'))
          setLoading(false)
        })
        .catch(err => {
          setLoading(false)
          sendErrorReport(err, 'Cannot fetch customer accounts list')
        })
    },
    [companyId]
  )

  const refreshUserGroups = () => {
    getUserGroups(page, activeQuery, searchBy, currentSort, rowsPerPage, metadataFilterQuery)
  }

  useEffect(() => {
    getUserGroups()
  }, [getUserGroups])

  const handleSearchSubmit = e => {
    e.preventDefault()
    setActiveQuery(query)
    setPage(0)
    getUserGroups(0, query, searchBy, currentSort, rowsPerPage, metadataFilterQuery)
  }

  const handleSearchClear = () => {
    setQuery('')
    setActiveQuery('')
    setPage(0)
    getUserGroups(0, undefined, undefined, currentSort, rowsPerPage, metadataFilterQuery)
  }

  const handlePageChange = newPage => {
    setPage(newPage)
    getUserGroups(newPage, activeQuery, searchBy, currentSort, rowsPerPage, metadataFilterQuery)
  }

  const handleSortChange = newSorted => {
    setSort(newSorted)
    getUserGroups(0, activeQuery, searchBy, newSorted, rowsPerPage, metadataFilterQuery)
  }

  const handlePageSizeChange = (newPageSize, newPage) => {
    setPage(newPage)
    setRowsPerPage(newPageSize)
    getUserGroups(newPage, activeQuery, searchBy, currentSort, newPageSize, metadataFilterQuery)
  }

  const redirectToUserGroupPage = rowData => {
    const userGroupID = get(rowData, 'original.id')
    history.push(`/${companyId}/license_users/user_groups/${userGroupID}`)
  }

  const handleManageCustomerClick = cb => {
    if (!canManageCustomers) {
      Notification(
        'error',
        <PermissionMissingNotificationTitle permission={userPermissions.customers_write} />,
        __('Contact you account admin for support.')
      )
      return false
    }
    cb(true)
    return true
  }

  const handleApplyMetadataFilter = filterQuery => {
    setMetadataFilterQuery(filterQuery)
    setPage(0)
    getUserGroups(0, activeQuery, searchBy, currentSort, rowsPerPage, filterQuery)
  }

  const handleClearMetadataFilter = () => {
    setMetadataFilterQuery('')
    setPage(0)
    getUserGroups(0, activeQuery, searchBy, currentSort, rowsPerPage, '')
  }

  const isAllowed = isFeatureAllowed(platformFeatures.extra_account_based_licensing)
  if (!isAllowed) {
    return (
      <Page title={__('User groups')}>
        <div className='UserGroups'>
          <Forbidden
            text={__('This feature is not available in your current plan type')}
            description={__('Contact sales if you wish to change this policy.')}
          />
        </div>
      </Page>
    )
  }

  return (
    <Page title={__('User groups')}>
      <div className='UserGroups'>
        <div className='UserGroups-public'>
          <div className='list-header'>
            <div>
              <div className='search-selector'>
                <Selector
                  handleChange={val => setSearchBy(val)}
                  options={[
                    { label: __('Search by all'), value: searchType.all },
                    { label: __('Search by code'), value: searchType.code },
                    { label: __('Search by name'), value: searchType.name },
                    {
                      label: __('Search by customer account name'),
                      value: searchType.customerAccountNameIcontains
                    },
                    {
                      label: __('Search by customer account code'),
                      value: searchType.customerAccountCodeIcontains
                    }
                  ]}
                  value={searchBy}
                />
              </div>
              <div className='search-container'>
                <form onSubmit={handleSearchSubmit} className='search-form'>
                  <TextInput handleChange={val => setQuery(val)} value={query} />
                  {activeQuery && (
                    <button type='button' onClick={handleSearchClear} disabled={isLoading}>
                      &times;
                    </button>
                  )}
                  <Button type='submit' theme='info' disabled={isLoading}>
                    <IconSearch fill='#fff' />
                  </Button>
                </form>
              </div>
            </div>
            <div>
              <Button theme='info' onClick={() => handleManageCustomerClick(setFormDisplay)}>
                {__('Add user group')}
              </Button>
            </div>
          </div>
          <Button
            type='button'
            className='AdvancedSearch-btn'
            onClick={() => setMetadataFilterDisplayed(!isMetadataFilterDisplayed)}
          >
            <IconFilter stroke='#2e86de' strokeWidth={2} fill='none' viewBox='0 0 24 24' />
            {__('Metadata filters')}
          </Button>
          {isMetadataFilterDisplayed && (
            <div className='metadata-filter-container'>
              <MetadataFilter
                onApplyFilter={handleApplyMetadataFilter}
                onClear={handleClearMetadataFilter}
              />
            </div>
          )}
          <List
            data={list}
            defaultSorted={currentSort}
            loading={isLoading}
            manual
            minRows={Math.ceil(get(list, 'length')) || 10}
            page={page}
            pages={Math.ceil(listCount / rowsPerPage)}
            onPageChange={handlePageChange}
            onSortedChange={handleSortChange}
            showPagination
            showPageSizeOptions
            pageSize={rowsPerPage}
            onPageSizeChange={(pageSize, pageIndex) => handlePageSizeChange(pageSize, pageIndex)}
            clickable
            handleClick={rowData => redirectToUserGroupPage(rowData)}
            columns={[
              {
                accessor: 'name',
                Header: __('Name'),
                Cell: cellInfo => displayValue(cellInfo.value)
              },
              {
                accessor: 'code',
                Header: __('Code'),
                Cell: cellInfo => displayValue(cellInfo.value)
              },
              {
                accessor: 'description',
                Header: __('Description'),
                Cell: cellInfo => displayValue(cellInfo.value)
              },
              {
                accessor: 'metadata',
                Header: __('Metadata'),
                Cell: cellInfo => displayValue(JSON.stringify(cellInfo.value))
              }
            ]}
          />
        </div>
        {isFormDisplayed && (
          <UserGroupForm
            closeCb={() => setFormDisplay(false)}
            confirmCb={() => {
              setFormDisplay(false)
              refreshUserGroups()
            }}
          />
        )}
      </div>
    </Page>
  )
}

export default UserGroups
